<template>
  <v-container class="ma-0 pa-0 mw-100" id="webviewer" ref="viewer" :style="viewerHeight"></v-container>
</template>

<script>
import WebViewer from "@pdftron/pdfjs-express";
import { log } from "../../../helpers/logger";

export default {
  name: "editor-preview",
  props: {
    initialDoc: String,
    file_name: String,
    file_id: String,
    xfdf_signatures: Array,
    xfdf_annotations: Array,
    xfdf: String,
  },
  data() {
    return {
      annotManager: null,
      xmlDocBase: null,
      parser: null,
    };
  },
  created: async function () {
    this.$emit("loading", true);
  },
  mounted: function () {
    this.openEditor(this);
  },
  watch: {
    initialDoc: async function () {
      if (this.annotManager) {
        const annots = this.annotManager.getAnnotationsList();

        this.annotManager.deleteAnnotations(annots);

        this.editor_instance.loadDocument(this.bufferToBlob(this.initialDoc), {
          filename: this.file_name,
        });
        log(
          "ui.other.EditorPreview.initialDoc - load Document in Editor",
          "info"
        );

        var base_xfdf =
          '<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><fields></fields><annots></annots></xfdf>';
        var parser_base = new DOMParser();
        var xmlDocBase = parser_base.parseFromString(base_xfdf, "text/xml");
        var parser = new DOMParser();

        // add signatures
        if (this.xfdf_signatures) {
          this.xfdf_signatures.forEach((signature) => {
            var xmlDocSignature = parser
              .parseFromString(signature.xfdf, "text/xml")
              .getElementsByTagName("freetext")[0];
            xmlDocBase
              .getElementsByTagName("annots")[0]
              .appendChild(xmlDocSignature);
          });
          log(
            "ui.other.EditorPreview.initialDoc - added annotations from XFDF",
            "info"
          );
        }

        const { documentViewer } = this.editor_instance.Core;
        documentViewer.addEventListener("documentLoaded", () => {
          this.annotManager.importAnnotations(xmlDocBase);
        });
      }
    },
  },
  computed: {
    currentWorkflow() {
      return this.$store.state.auth.workflow;
    },
    currentOrganization() {
      return this.$store.state.auth.organization;
    },
    viewerHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return { "--height": "400px" };
        case "sm":
          return { "--height": "600px" };
        case "md":
          return { "--height": "800px" };
        case "lg":
          return { "--height": "1000px" };
        case "xl":
          return { "--height": "1200px" };
      }
    },
  },
  methods: {
    bufferToBlob(buffer) {
      if (buffer) {
        let d = JSON.parse(buffer).toString("binary");
        let l = d.length;
        let array = new Uint8Array(l);
        for (let i = 0; i < l; i++) {
          array[i] = d.charCodeAt(i);
        }
        log(
          "ui.other.EditorPreview.bufferToBlob - converted buffer to blob",
          "info"
        );
        return new Blob([array], { type: "application/octet-stream" });
      }
    },
    addSignatureFields() {
      if (this.xfdf_signatures) {
        this.xfdf_signatures.forEach((signature) => {
          var xmlDocSignature = this.parser
            .parseFromString(signature.xfdf, "text/xml")
            .getElementsByTagName("freetext")[0];
          this.xmlDocBase
            .getElementsByTagName("annots")[0]
            .appendChild(xmlDocSignature);
        });
      }
    },
    addXfdfElements() {
      let add_xml = this.parser
        .parseFromString(this.xfdf, "text/xml")
        .querySelectorAll(
          "fileattachment, stamp, highlight, underline, strikeout, squiggly, square, text, circle, polygon, line, polyline"
        );

      for (let i = 0; i < add_xml.length; i++) {
        this.xmlDocBase
          .getElementsByTagName("annots")[0]
          .appendChild(add_xml[i]);
      }
    },
    async openEditor(app) {
      const path = `${process.env.BASE_URL}lib`;

      WebViewer(
        {
          path: path,
          disabledElements: ["toggleNotesButton", "searchButton", "menuButton"],
          licenseKey: "PqwNd91gcw7LXw11DtVo",
        },
        this.$refs.viewer
      ).then((instance) => {
        const { Annotations } = instance;
        const { documentViewer, annotationManager } = instance.Core;

        this.annotManager = annotationManager;
        this.annotManager.enableReadOnlyMode();
        this.annotManager.disableRedaction();
        this.instance = instance;

        instance.setTheme("light");
        instance.UI.setLanguage(this.$t.locale);

        this.editor_instance = instance;

        instance.loadDocument(this.bufferToBlob(this.initialDoc), {
          filename: app.file_name,
        });
        log(
          "ui.other.EditorPreview.openEditor - load Document in Editor",
          "info"
        );

        // base xfdf string for non visible annotations
        var base_xfdf =
          '<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><fields></fields><annots></annots></xfdf>';
        var parser_base = new DOMParser();
        this.xmlDocBase = parser_base.parseFromString(base_xfdf, "text/xml");
        this.parser = new DOMParser();

        // form field listener that extract form data at input
        this.annotManager.addEventListener("fieldChanged", (field, value) => {
          let annotation_signer_id = field.rd.split("_")[0];
          let annotation_id = field.rd.split("_")[1];

          if (annotation_signer_id == app.currentWorkflow.signer_id) {
            let annot = this.xfdf_annotations.filter(
              (annotation) => annotation.annotation_id == annotation_id
            )[0];
            annot.value = value;
          }

          this.getNewXfdf();
        });

        documentViewer.addEventListener("documentLoaded", () => {
          this.addSignatureFields();
          this.addXfdfElements();
          this.annotManager.importAnnotations(this.xmlDocBase);

          this.annotManager.selectAnnotations(Annotations);
        });

        Annotations.WidgetAnnotation.getCustomStyles = (widget) => {
          if (
            widget.fieldName.split("_")[0] == app.currentWorkflow.signer_id
          ) {
            widget.fieldFlags.set("ReadOnly", false);
            return {
              "background-color": "lightgreen",
            };
          }
        };

        /*
        // enable signer specific form fields
        documentViewer.addEventListener("annotationsLoaded", () => {
          Annotations.WidgetAnnotation.getCustomStyles = (widget) => {
            if (
              widget.fieldName.split("_")[0] == app.currentWorkflow.signer_id
            ) {
              widget.fieldFlags.set("ReadOnly", false);
              return {
                "background-color": "lightgreen",
              };
            }
          };
        });*/
      });
    },
    getNewXfdf() {
      this.annotManager.exportAnnotations().then((xfdfData) => {
        this.$emit("updateXfdf", xfdfData);
      });
    },
  },
};
</script>
<style scoped>
#webviewer {
  height: var(--height);
}
</style>
